import { clsx, type ClassValue } from "clsx"
import {
  createTailwindMerge,
  getDefaultConfig,
  mergeConfigs,
} from "tailwind-merge"

/**
 * Project-aware class merger.
 *
 * Custom font-size / weight / radius / shadow scales live outside Tailwind
 * defaults. Without teaching twMerge about them:
 *   cn("text-body1", "text-neutral-text-500") → drops the size
 *   cn("rounded-m", "rounded-l")               → keeps both
 *
 * Extra wrinkle: Tailwind's directional `rounded-l` (left side) steals our
 * size token `rounded-l` (24px). We restrict directional groups to explicit
 * sizes (`rounded-l-m`, `rounded-l-full`, …) so bare `rounded-l` is a size.
 */
const twMerge = createTailwindMerge(() => {
  const base = getDefaultConfig()
  const sides = [
    "rounded-s",
    "rounded-e",
    "rounded-t",
    "rounded-r",
    "rounded-b",
    "rounded-l",
    "rounded-ss",
    "rounded-se",
    "rounded-ee",
    "rounded-es",
    "rounded-tl",
    "rounded-tr",
    "rounded-br",
    "rounded-bl",
  ] as const

  // No empty suffix on directional groups — that would steal bare `rounded-l`.
  const directionalOverrides = Object.fromEntries(
    sides.map((side) => [side, [{ [side]: ["none", "full", "s", "m", "l"] }]]),
  )

  return mergeConfigs(base, {
    override: {
      classGroups: {
        rounded: [{ rounded: ["", "none", "full", "s", "m", "l"] }],
        ...directionalOverrides,
      },
    },
    extend: {
      classGroups: {
        "font-size": [
          { text: ["h1", "h2", "h3", "body1", "body2", "caption", "micro"] },
        ],
        "font-weight": [{ font: ["regular", "medium"] }],
        shadow: [{ shadow: ["s", "m", "l"] }],
      },
    },
  })
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
